import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { trendsAPI } from "../lib/api";
import { getCachedTrends, prefetchTrends } from "../lib/prefetch";
import { useTrendStore } from "../lib/store";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  TrendingUp,
  Flame,
  Hash,
  Sparkles,
  ArrowRight,
  Zap,
  RefreshCw,
  Clock,
  Globe,
  Filter,
  X,
  Search,
} from "lucide-react";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// All possible categories
const ALL_CATEGORIES = [
  "All",
  "Lifestyle",
  "Entertainment", 
  "Fashion",
  "Beauty",
  "Food",
  "Fitness",
  "Tech",
  "Finance",
  "Travel",
  "Comedy",
  "News",
  "Stories",
  "Vlog",
  "Trending",
];

export default function TrendsPage() {
  const navigate = useNavigate();
  const { trends, setTrends } = useTrendStore();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  // Get unique categories from current trends
  const availableCategories = useMemo(() => {
    const categories = new Set(trends.map(t => t.category));
    return ["All", ...Array.from(categories).sort()];
  }, [trends]);

  // Filter trends by selected category AND search query
  const filteredTrends = useMemo(() => {
    let filtered = trends;
    
    // Filter by category
    if (selectedCategory !== "All") {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(t => 
        t.title.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query) ||
        t.hashtags.some(h => h.toLowerCase().includes(query)) ||
        t.hook_suggestion?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [trends, selectedCategory, searchQuery]);

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const fetchTrends = useCallback(async (forceRefresh = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
      }
      
      // Check for cached data first (instant load)
      if (!forceRefresh) {
        const cached = getCachedTrends();
        if (cached && cached.length > 0) {
          console.log('[Trends] Using cached data:', cached.length, 'items');
          setTrends(cached);
          setLoading(false);
          // Refresh in background silently
          prefetchTrends(false).then(freshData => {
            if (freshData && freshData.length > 0) {
              setTrends(freshData);
              setLastUpdated(new Date());
            }
          }).catch(() => {});
          return;
        }
      }
      
      // No cache or force refresh - fetch from API
      const response = await trendsAPI.getTrends(forceRefresh);
      setTrends(response.data);
      setLastUpdated(new Date());
      if (forceRefresh) {
        toast.success("Trends refreshed!", {
          description: "Fetched latest trends from Google Trends"
        });
      }
    } catch (error) {
      console.error("Failed to fetch trends:", error);
      toast.error("Failed to fetch trends", {
        description: "Using cached data instead"
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [setTrends]);

  useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  const handleRefresh = () => {
    fetchTrends(true);
  };

  const handleUseTrend = (trend) => {
    navigate("/create", { state: { trend } });
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  const getCategoryColor = (category) => {
    const colors = {
      Lifestyle: "bg-[#00F0FF]/20 text-[#00F0FF]",
      Vlog: "bg-[#A855F7]/20 text-[#A855F7]",
      Entertainment: "bg-[#FF0055]/20 text-[#FF0055]",
      Comedy: "bg-[#F59E0B]/20 text-[#F59E0B]",
      Commentary: "bg-[#10B981]/20 text-[#10B981]",
      Fashion: "bg-[#EC4899]/20 text-[#EC4899]",
      Beauty: "bg-[#8B5CF6]/20 text-[#8B5CF6]",
      Stories: "bg-[#06B6D4]/20 text-[#06B6D4]",
      Trending: "bg-[#00F0FF]/20 text-[#00F0FF]",
      Food: "bg-[#F97316]/20 text-[#F97316]",
      Fitness: "bg-[#22C55E]/20 text-[#22C55E]",
      Tech: "bg-[#3B82F6]/20 text-[#3B82F6]",
      Finance: "bg-[#84CC16]/20 text-[#84CC16]",
      Travel: "bg-[#14B8A6]/20 text-[#14B8A6]",
      News: "bg-[#EF4444]/20 text-[#EF4444]",
    };
    return colors[category] || "bg-muted text-muted-foreground";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
      data-testid="trends-page"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-[#00F0FF]" />
            Trending Now
          </h1>
          <div className="flex items-center gap-4 mt-2">
            <p className="text-muted-foreground">
              Real-time trends from Google Trends with AI-generated hooks
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Globe className="w-3 h-3 text-[#00F0FF]" />
              <span>Live Data</span>
            </div>
          </div>
          {lastUpdated && (
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span>Updated {lastUpdated.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          variant="outline"
          className="border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-xl gap-2"
          data-testid="refresh-trends-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh Trends'}
        </Button>
      </div>

      {/* Search Bar */}
      <motion.div variants={itemVariants} className="relative">
        <div className="relative flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search trends by keyword, hashtag, or topic..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-10 h-12 bg-secondary/50 border-border/50 rounded-xl text-white placeholder:text-muted-foreground focus:border-[#00F0FF]/50"
              data-testid="trends-search-input"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 rounded-full bg-secondary hover:bg-secondary/80 flex items-center justify-center transition-colors"
                data-testid="clear-search-btn"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>
        {searchQuery && (
          <p className="text-xs text-muted-foreground mt-2">
            Found {filteredTrends.length} trend{filteredTrends.length !== 1 ? 's' : ''} matching &quot;{searchQuery}&quot;
          </p>
        )}
      </motion.div>

      {/* Category Filter */}
      <motion.div variants={itemVariants} className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Filter className="w-4 h-4" />
          <span>Filter by category</span>
          {selectedCategory !== "All" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedCategory("All")}
              className="h-6 px-2 text-xs text-muted-foreground hover:text-white"
            >
              <X className="w-3 h-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {availableCategories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              size="sm"
              onClick={() => handleCategorySelect(category)}
              className={`rounded-full transition-all ${
                selectedCategory === category
                  ? "bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"
                  : "border-border/50 text-muted-foreground hover:text-white hover:border-[#00F0FF]/50"
              }`}
              data-testid={`category-filter-${category.toLowerCase()}`}
            >
              {category}
              {category !== "All" && (
                <span className="ml-1.5 text-xs opacity-70">
                  ({trends.filter(t => t.category === category).length})
                </span>
              )}
            </Button>
          ))}
        </div>
      </motion.div>

      {/* Top Trends Banner - Only show when viewing All or matching category */}
      {filteredTrends.length > 0 && (selectedCategory === "All" || filteredTrends[0]?.category === selectedCategory) && (
        <motion.div variants={itemVariants}>
          <Card className="bg-gradient-to-r from-[#FF0055]/20 to-[#00F0FF]/20 border-border/50 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#00F0FF]/10 rounded-full blur-3xl" />
            <CardContent className="p-8 relative">
              <div className="flex items-center gap-2 mb-4">
                <Flame className="w-5 h-5 text-[#FF0055]" />
                <span className="text-[#FF0055] font-semibold text-sm uppercase tracking-wider">
                  {selectedCategory === "All" ? "Hot Right Now" : `Top in ${selectedCategory}`}
                </span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">{filteredTrends[0]?.title}</h2>
              <p className="text-muted-foreground max-w-xl">{filteredTrends[0]?.description}</p>
              <div className="flex items-center gap-4 mt-6">
                <Button
                  onClick={() => handleUseTrend(filteredTrends[0])}
                  className="bg-[#FF0055] hover:bg-[#FF0055]/90 text-white font-bold rounded-xl gap-2 neon-glow-pink"
                  data-testid="use-top-trend-btn"
                >
                  <Zap className="w-4 h-4" />
                  Use This Trend
                </Button>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Sparkles className="w-4 h-4" />
                  <span>Viral Score: {filteredTrends[0]?.viral_score}%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* No Results Message */}
      {filteredTrends.length === 0 && (
        <motion.div 
          variants={itemVariants}
          className="text-center py-12"
        >
          <div className="text-muted-foreground mb-4">
            {searchQuery ? (
              <>
                <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No trends found for &quot;{searchQuery}&quot;</p>
                <p className="text-sm">Try different keywords or clear your search</p>
              </>
            ) : (
              <>
                <Filter className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No trends found in &quot;{selectedCategory}&quot;</p>
                <p className="text-sm">Try selecting a different category or refresh to get new trends</p>
              </>
            )}
          </div>
          <div className="flex gap-3 justify-center">
            {searchQuery && (
              <Button
                onClick={handleClearSearch}
                variant="outline"
                className="border-[#FF0055]/50 text-[#FF0055] hover:bg-[#FF0055]/10 rounded-xl"
              >
                Clear Search
              </Button>
            )}
            <Button
              onClick={() => { setSelectedCategory("All"); setSearchQuery(""); }}
              variant="outline"
              className="border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-xl"
            >
              View All Trends
            </Button>
          </div>
        </motion.div>
      )}

      {/* Trends Grid */}
      <AnimatePresence mode="wait">
        <motion.div 
          key={selectedCategory}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredTrends.slice(1).map((trend, index) => (
          <motion.div key={trend.id} variants={itemVariants}>
            <Card className="bg-card border-border/50 card-hover h-full flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between mb-2">
                  <Badge className={getCategoryColor(trend.category)}>{trend.category}</Badge>
                  <div className="flex items-center gap-1 text-sm">
                    <Flame className="w-4 h-4 text-[#FF0055]" />
                    <span className="text-white font-medium">{trend.viral_score}%</span>
                  </div>
                </div>
                <CardTitle className="text-lg font-semibold text-white">{trend.title}</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <p className="text-muted-foreground text-sm mb-4 flex-1">{trend.description}</p>
                
                {/* Viral Score Bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-muted-foreground">Viral Potential</span>
                    <span className="text-white font-medium">{trend.viral_score}%</span>
                  </div>
                  <Progress value={trend.viral_score} className="h-2 bg-secondary" />
                </div>

                {/* Hashtags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {trend.hashtags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground flex items-center gap-1"
                    >
                      <Hash className="w-3 h-3" />
                      {tag.replace("#", "")}
                    </span>
                  ))}
                </div>

                {/* Hook Suggestion */}
                <div className="p-3 bg-secondary/50 rounded-lg mb-4">
                  <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Hook Suggestion
                  </p>
                  <p className="text-sm text-white/90">{trend.hook_suggestion}</p>
                </div>

                <Button
                  onClick={() => handleUseTrend(trend)}
                  variant="outline"
                  className="w-full border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-xl gap-2"
                  data-testid={`use-trend-btn-${index}`}
                >
                  Use Trend <ArrowRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ))}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}
